import { motion } from 'framer-motion';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Zap, Building2, MapPin, BarChart3, Receipt, Route,
  Globe, PenLine, Activity, Calculator, ClipboardList, Users,
} from 'lucide-react';
import { features, fadeInUp, stagger } from './data';

const COMPANY_FEATURES = [
  { icon: BarChart3, title: 'Advanced Reports', description: 'Machine accounting, partner payouts, true P&L, and referrer commission reports. Filter by company, location, or date range. Export CSV anytime.', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  { icon: Receipt, title: 'Tax Reporting', description: 'Quarterly and annual tax-ready reports per company. Revenue, expenses, deductions, net taxable income, and estimated sales tax — all in one view.', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' },
  { icon: Route, title: 'Route Planning', description: 'Plan efficient collection routes with Google Maps. Drag-to-reorder stops, assign drivers, and optimize travel time across all your locations.', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
  { icon: Globe, title: 'Multi-Language Support', description: 'Run your operation in English or Spanish. The entire dashboard, sidebar, and login page switch instantly. More languages coming soon.', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
  { icon: PenLine, title: 'Digital Signatures & Receipts', description: 'Location owners sign off on collections right from their device. View, print, and save collection receipts as PDF.', color: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
  { icon: Activity, title: 'Audit Trail', description: 'Full change tracking across locations, machines, collections, expenses, and users. Know who changed what and when — complete accountability.', color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20' },
];

const LOCATION_FEATURES = [
  { icon: Calculator, title: 'Daily & Monthly Accounting', description: 'Record money in/out per machine, auto-calculate net profit, track period and lifetime collections — all from your phone.', color: 'text-teal-400', bg: 'bg-teal-500/10' },
  { icon: ClipboardList, title: 'Shift-Based Bookkeeping', description: 'Manage shift-wise machine readings with bulk data entry. Over/short calculations, credits tracking, and CSV export built in.', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { icon: Users, title: 'Employee Management', description: 'Create employee accounts with shift assignments. Restrict access so staff only sees their shift\'s bookkeeping data.', color: 'text-purple-400', bg: 'bg-purple-500/10' },
];

export const FeaturesSection = () => (
  <section id="features" aria-label="SlotFleet Features" className="py-20 md:py-32">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        className="text-center max-w-3xl mx-auto mb-16"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={stagger}
      >
        <motion.p variants={fadeInUp} className="text-amber-400 font-semibold mb-4">
          POWERFUL FEATURES
        </motion.p>
        <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-bold mb-6 text-white">
          Built for Every Side of Your Business
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-gray-400 text-lg">
          Whether you run a single location or manage a fleet across the state,
          SlotFleet has the tools you need.
        </motion.p>
      </motion.div>

      {/* Core Platform */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
        variants={stagger}
        className="mb-20"
      >
        <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
            <Zap className="w-5 h-5 text-yellow-400" />
          </div>
          <h3 className="text-2xl font-bold text-white">Core Platform</h3>
          <div className="flex-1 h-px bg-gradient-to-r from-yellow-500/30 to-transparent ml-4" />
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, idx) => (
            <motion.div key={feature.title} variants={fadeInUp} transition={{ delay: idx * 0.08 }}>
              <Card className="bg-neutral-900 border-white/5 hover:border-amber-500/30 transition-all h-full group">
                <CardContent className="p-6">
                  <div className={`w-11 h-11 rounded-lg ${feature.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <feature.icon className={`w-5 h-5 ${feature.color}`} />
                  </div>
                  <h4 className="text-lg font-bold mb-2 text-white">{feature.title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* For Companies */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
        variants={stagger}
        className="mb-20 relative"
      >
        <div className="absolute -inset-6 bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-transparent rounded-3xl blur-xl pointer-events-none" />
        <div className="relative bg-neutral-900/40 border border-amber-500/15 rounded-2xl p-6 md:p-10">
          <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 mb-1 text-xs">ENTERPRISE TOOLS</Badge>
              <h3 className="text-2xl md:text-3xl font-bold text-white">For Companies</h3>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-amber-500/30 to-transparent ml-4" />
          </motion.div>
          <motion.p variants={fadeInUp} className="text-gray-400 mb-8 max-w-2xl">
            Powerful reporting, compliance, and oversight tools designed for multi-location operators and company administrators.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {COMPANY_FEATURES.map((feature, index) => (
              <motion.div key={feature.title} variants={fadeInUp} transition={{ delay: index * 0.1 }}>
                <Card className={`bg-black/60 ${feature.border} hover:border-amber-500/40 transition-all h-full group`}>
                  <CardContent className="p-6 md:p-7">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl ${feature.bg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                        <feature.icon className={`w-6 h-6 ${feature.color}`} />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold mb-2 text-white">{feature.title}</h4>
                        <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* For Locations */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
        variants={stagger}
      >
        <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-teal-400" />
          </div>
          <h3 className="text-2xl font-bold text-white">For Locations</h3>
          <div className="flex-1 h-px bg-gradient-to-r from-teal-500/30 to-transparent ml-4" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {LOCATION_FEATURES.map((feature, index) => (
            <motion.div key={feature.title} variants={fadeInUp} transition={{ delay: index * 0.1 }}>
              <Card className="bg-neutral-900 border-white/5 hover:border-teal-500/30 transition-all h-full group">
                <CardContent className="p-6">
                  <div className={`w-11 h-11 rounded-lg ${feature.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <feature.icon className={`w-5 h-5 ${feature.color}`} />
                  </div>
                  <h4 className="text-lg font-bold mb-2 text-white">{feature.title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);
